import React from 'react';
import { Modal } from 'antd';
import styles from './testReportModal.module.scss';
import sectionType from '../../utils/Enum/sectionType';
import CircleProgress from './CircleProgress/CircleProgress';

const TestReportModal = ({ visible, onClose, reportData }) => {
    // Calculate overall score color based on percentage
    const getScoreColor = (percentage) => {
        if (percentage >= 80) return '#22c55e'; // Green
        if (percentage >= 60) return '#eab308'; // Yellow
        return '#ef4444'; // Red
    };

    // Calculate category scores (Math, Reading & Writing)
    const getCategoryScore = (sections) => {
        if (!reportData || !sections.length) return { percentage: 0, correct: 0, total: 0 };

        let totalCorrect = 0;
        let totalQuestions = 0;

        sections.forEach(section => {
            if (reportData.sectionScores && reportData.sectionScores[section]) {
                totalCorrect += reportData.sectionScores[section].correct || 0;
                totalQuestions += reportData.sectionScores[section].total || 0;
            }
        });

        const percentage = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
        return { percentage, correct: totalCorrect, total: totalQuestions };
    };

    // Math sections
    const mathSections = [
        sectionType.ALGEBRA,
        sectionType.ADVANCED_MATH,
        sectionType.PROBLEM_SOLVING_AND_DATA_ANALYSIS,
        sectionType.GEOMETRY_AND_TRIGONOMETRY
    ];

    // Reading & Writing sections
    const readingWritingSections = [
        sectionType.INFORMATION_AND_IDEAS,
        sectionType.CRAFT_AND_STRUCTURE,
        sectionType.EXPRESSION_OF_IDEAS,
        sectionType.STANDARD_ENGLISH_CONVENTIONS
    ];

    const mathScore = getCategoryScore(mathSections);
    const readingWritingScore = getCategoryScore(readingWritingSections);

    // Overall score
    const overallScore = reportData ? {
        percentage: reportData.overallPercentage || 0,
        correct: reportData.totalCorrect || 0,
        total: reportData.totalQuestions || 0
    } : { percentage: 0, correct: 0, total: 0 };

    if (!reportData) {
        return (
            <Modal
                title="Test Report"
                open={visible}
                onCancel={onClose}
                footer={null}
                width={800}
                className={styles.testReportModal}
            >
                <div className={styles.noDataMessage}>
                    <div className={styles.noDataIcon}>📊</div>
                    <h3>No Test Report Available</h3>
                    <p>Complete a full test to see your detailed performance report.</p>
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            title="Test Performance Report"
            open={visible}
            onCancel={onClose}
            footer={null}
            width={900}
            className={styles.testReportModal}
        >
            <div className={styles.reportContent}>
                {/* Overall Score Section */}
                <div className={styles.overallSection}>
                    <div className={styles.overallScore}>
                        <CircleProgress
                            percentage={overallScore.percentage}
                            label="SAT Score"
                            size={150}
                            displayMode="sat-score"
                            correct={overallScore.correct}
                            total={overallScore.total}
                        />
                        <div className={styles.scoreDetails}>
                            <h3>Overall Performance</h3>
                            <div className={styles.scoreText}>
                                {overallScore.correct} of {overallScore.total} correct ({overallScore.percentage}%)
                            </div>
                            <div className={styles.testDate}>
                                Test Date: {reportData.testDate ? new Date(reportData.testDate).toLocaleDateString() : 'N/A'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Category Scores */}
                <div className={styles.categoryScores}>
                    <div className={styles.categoryItem}>
                        <CircleProgress
                            percentage={mathScore.percentage}
                            label="Math Section"
                            size={100}
                            displayMode="section-score"
                            correct={mathScore.correct}
                            total={mathScore.total}
                            sectionType="math"
                        />
                        <div className={styles.categoryDetails}>
                            <div className={styles.scoreText}>
                                {mathScore.correct}/{mathScore.total} ({mathScore.percentage}%)
                            </div>
                        </div>
                    </div>
                    <div className={styles.categoryItem}>
                        <CircleProgress
                            percentage={readingWritingScore.percentage}
                            label="Reading & Writing"
                            size={100}
                            displayMode="section-score"
                            correct={readingWritingScore.correct}
                            total={readingWritingScore.total}
                            sectionType="reading-writing"
                        />
                        <div className={styles.categoryDetails}>
                            <div className={styles.scoreText}>
                                {readingWritingScore.correct}/{readingWritingScore.total} ({readingWritingScore.percentage}%)
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section Breakdown */}
                <div className={styles.sectionBreakdown}>
                    <h3>Section Performance</h3>

                    {/* Math Sections */}
                    <div className={styles.sectionCategory}>
                        <h4>Mathematics</h4>
                        <div className={styles.sectionsGrid}>
                            {mathSections.map(section => {
                                const sectionData = reportData.sectionScores?.[section] || { correct: 0, total: 0, percentage: 0 };
                                return (
                                    <div key={section} className={styles.sectionCard}>
                                        <div className={styles.sectionHeader}>
                                            <span className={styles.sectionName}>{section}</span>
                                            <span
                                                className={styles.sectionScore}
                                                style={{ color: getScoreColor(sectionData.percentage) }}
                                            >
                                                {sectionData.percentage}%
                                            </span>
                                        </div>
                                        <div className={styles.sectionDetails}>
                                            {sectionData.correct} of {sectionData.total} correct
                                        </div>
                                        <div className={styles.progressBar}>
                                            <div
                                                className={styles.progressFill}
                                                style={{
                                                    width: `${sectionData.percentage}%`,
                                                    backgroundColor: getScoreColor(sectionData.percentage)
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Reading & Writing Sections */}
                    <div className={styles.sectionCategory}>
                        <h4>Reading & Writing</h4>
                        <div className={styles.sectionsGrid}>
                            {readingWritingSections.map(section => {
                                const sectionData = reportData.sectionScores?.[section] || { correct: 0, total: 0, percentage: 0 };
                                return (
                                    <div key={section} className={styles.sectionCard}>
                                        <div className={styles.sectionHeader}>
                                            <span className={styles.sectionName}>{section}</span>
                                            <span
                                                className={styles.sectionScore}
                                                style={{ color: getScoreColor(sectionData.percentage) }}
                                            >
                                                {sectionData.percentage}%
                                            </span>
                                        </div>
                                        <div className={styles.sectionDetails}>
                                            {sectionData.correct} of {sectionData.total} correct
                                        </div>
                                        <div className={styles.progressBar}>
                                            <div
                                                className={styles.progressFill}
                                                style={{
                                                    width: `${sectionData.percentage}%`,
                                                    backgroundColor: getScoreColor(sectionData.percentage)
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default TestReportModal;